import React, { useEffect, useMemo, useRef, useState } from "react";
import Tabs from "../common/Tabs";
import DiscoverCard from "../home/common/DiscoverCard";
import WonAuctionsCarousel from "./WonAuctionsCarousel";
import { Pagination } from "../common/Pagination";
import {
    discoverFilters,
    discoverTabs,
    // ADREC: sortOptions orphaned by the removed "Sort by" control.
    // sortOptions,
} from "../../utils/constants";
import { useDispatch, useSelector } from "react-redux";
import { handleDiscoverAuctions } from "../../redux/action/buyerAction";
import {
    handleDiscoverLoading,
    resetFilter,
    setActiveTab,
    setDiscoverData,
    setFilter,
    setPage,
    toggleDiscoverModal,
    toggleFilterModal,
} from "../../redux/slice/discoverSlice";
// ADREC: orphaned by the removed free-text search box.
// import SearchInput from "../common/SearchInput";
import { getTotalPages } from "../../helpers";
import useTranslationHook from "../hooks/useTranslationHook";
import useSocketSync from "../hooks/useSocketSync";
import {
    sellerAction,
    setCities,
    setConstructionStatus,
    setDistricts,
    setMunicipalities,
    setPropertyType,
} from "../../redux/action/sellerAction";
// ADREC: orphaned by the removed All Filters modal.
// import FilterModal from "./FilterModal";
import ReactSelect from "../common/ReactSelect";
// ADREC: orphaned by the removed map view.
// import MapView from "./MapView";
// ADREC: useNavigate orphaned by the removed view toggle (handleViewChange was
// its only caller). Original:
// import { useNavigate, useSearchParams } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

const Discover = () => {
    // const navigate = useNavigate();  // ADREC: orphaned with handleViewChange
    const dispatch = useDispatch();
    const { t } = useTranslationHook();
    const mapRef = useRef(null);
    const [params, setParams] = useSearchParams();
    // ADREC: "map" removed -- map view is gone from Discover, so ?view=map is no
    // longer a valid view and falls through to the grid default below.
    // const allowedViews = ["list", "grid", "map"];
    const allowedViews = ["list", "grid"];
    const viewParam = params.get("view");
    const isValid = allowedViews.includes(viewParam);
    const municipalities = useSelector((state) => state.seller.municipalities);
    const districts = useSelector((state) => state.seller.districts);
    const user = useSelector((state) => state.auth.user);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const isSeller = useSelector((state) => state.profile.account);
    const activeTab = useSelector((state) => state.discover.activeTab);
    const discoverData = useSelector((state) => state.discover.discoverData);
    // ADREC: orphaned by the removed All Filters modal.
    // const isFilterModalOpen = useSelector(
    //     (state) => state.discover.isFilterModalOpen
    // );
    const isClusterPropertyListOpen = useSelector(
        (state) => state.discover.isClusterPropertyListOpen
    );

    const construction_status = useSelector(
        (state) => state.discover.construction_status
    );
    const mapZoom = useSelector((state) => state.discover.mapZoom);
    const mapBound = useSelector((state) => state.discover.mapBound);
    const search = useSelector((state) => state.discover.search);

    const city = useSelector((state) => state.discover.city);
    const municipality = useSelector((state) => state.discover.municipality);
    const district = useSelector((state) => state.discover.district);
    const property_type = useSelector((state) => state.discover.property_type);
    const status = useSelector((state) => state.discover.status);
    const property_for = useSelector((state) => state.discover.property_for);

    const cities = useSelector((state) => state.seller.cities);
    const propertyTypes = useSelector((state) => state.seller.propertyTypes);
    const constructionStatus = useSelector(
        (state) => state.seller.constructionStatus
    );

    const filter_beds = useSelector((state) => state.discover.filter_beds);
    const clusterPropertyData = useSelector(
        (state) => state.discover.clusterPropertyData
    );
    const filter_baths = useSelector((state) => state.discover.filter_baths);
    // const search = useSelector((state) => state.discover.search);
    const total = useSelector((state) => state.discover.total);
    const pageSize = useSelector((state) => state.discover.page.pageSize);
    const currentPage = useSelector((state) => state.discover.page.currentPage);
    const discoverLoading = useSelector(
        (state) => state.discover.discoverLoading
    );
    // ADREC: setSortBy orphaned by the removed "Sort by" control. `sortBy` itself
    // is kept because fetchDiscover still spreads `sortBy?.api` into the request.
    // const [sortBy, setSortBy] = useState(null);
    const [sortBy] = useState(null);

    const totalPages = useMemo(() => {
        return getTotalPages(total, pageSize);
    }, [total, pageSize]);

    useEffect(() => {
        if (!isValid) {
            params.set("view", "grid");
            setParams(params, { replace: true });
        }
    }, [isValid, params, setParams]);

    const view = isValid ? viewParam : "grid";

    useEffect(() => {
        if (!constructionStatus || constructionStatus.length === 0) {
            dispatch(setConstructionStatus());
        }
        if (!propertyTypes || propertyTypes?.length === 0)
            dispatch(setPropertyType());
        if (!cities || cities?.length === 0) dispatch(setCities());
        return () => dispatch(resetFilter());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (city) {
            dispatch(sellerAction.handleSetMunicipalities([]));
            dispatch(sellerAction.handleSetDistricts([]));
            dispatch(setMunicipalities({ state_id: city }));
            dispatch(
                setFilter({
                    municipality: null,
                    district: null,
                })
            );
        }
    }, [city]);

    useEffect(() => {
        if (municipality) {
            dispatch(sellerAction.handleSetDistricts([]));
            dispatch(setDistricts({ municipality_id: municipality }));
            dispatch(
                setFilter({
                    district: null,
                })
            );
        }
    }, [municipality]);

    const latestCallRef = useRef(0);
    const manualAbortRef = useRef(null);

    const fetchDiscover = async (signal) => {
        const callId = Date.now();
        latestCallRef.current = callId;

        dispatch(handleDiscoverLoading(true));

        const response = await dispatch(
            handleDiscoverAuctions(
                {
                    site_id: 3,
                    page: currentPage,
                    page_size: pageSize,
                    ...(sortBy?.api || {}),
                    ...(filter_beds.length && {
                        filter_beds: filter_beds.map((item) => item.value),
                    }),
                    ...(filter_baths.length && {
                        filter_baths: filter_baths.map((item) => item.value),
                    }),
                    ...(construction_status && { construction_status }),
                    ...(city && { city }),
                    ...(district && { district }),
                    ...(municipality && { municipality }),
                    ...(property_type && { property_type }),
                    // ...(status && status === "upcoming_listing" && { filter: "upcoming_listing" }),
                    // ADREC: when no status is picked (and not on the Live/Closed tabs),
                    // default to "active_recently_closed" so the Discover page shows ACTIVE
                    // auctions plus recently-closed ones (last 30 days) by default, instead
                    // of active-only. A picked status still overrides this.
                    ...(status
                        ? { filter: status }
                        : activeTab !== discoverTabs[1].key &&
                            activeTab !== discoverTabs[2].key
                          ? { filter: "active_recently_closed" }
                          : {}),
                    // ...(status && status !== "upcoming_listing" && { status }),
                    ...(property_for && { property_for }),
                    ...(user?.user_id && { user_id: Number(user.user_id) }),
                    // ...(activeTab !== discoverTabs[2].key && {
                    //     property_for: discoverTabs.find(
                    //         (tab) => tab.key === activeTab
                    //     )?.propertyFor,
                    // }),
                    ...(activeTab === discoverTabs[2].key && {
                        filter: discoverTabs[2].key,
                    }),
                    ...(activeTab === discoverTabs[1].key && { is_admin: 1 }),
                    ...(search && { search }),
                },
                signal
            )
        );

        if (callId === latestCallRef.current) {
            const { data } = response || {};
            dispatch(
                setDiscoverData({
                    data: data?.data || [],
                    total: data?.total || 0,
                })
            );
            dispatch(handleDiscoverLoading(false));
        }
    };

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;
        fetchDiscover(signal);
        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy?.id, activeTab, currentPage]);

    const handleManualSearch = () => {
        manualAbortRef.current?.abort();
        const controller = new AbortController();
        manualAbortRef.current = controller;

        fetchDiscover(controller.signal);
        dispatch(toggleDiscoverModal({ isClusterPropertyListOpen: false }));
        mapRef.current &&
            (() => {
                mapRef.current.setZoom(7);
                mapRef.current.panTo({ lat: 24.485, lng: 54.605 });
            })();
    };

    useEffect(() => () => manualAbortRef.current?.abort(), []);

    const [wonData, setWonData] = useState([]);

    useEffect(() => {
        const abortController = new AbortController();
        const signal = abortController.signal;

        if (isAuthenticated && isSeller === 0 && user?.user_id) {
            (async () => {
                const response = await dispatch(
                    handleDiscoverAuctions(
                        {
                            site_id: 3,
                            page: 1,
                            page_size: 50,
                            filter: "won",
                            user_id: Number(user.user_id),
                            bidder_id: Number(user.user_id),
                        },
                        signal
                    )
                );
                const { data } = response || {};
                setWonData(data?.data || []);
            })();
        } else {
            setWonData([]);
        }

        return () => abortController.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, isSeller, user?.user_id]);

    const wonSyncData = useSocketSync(
        wonData,
        user?.user_id,
        "wonAuctionsSync"
    );

    // ADREC: orphaned by the removed list/grid view toggle and Map View link.
    // const handleViewChange = (type) => {
    //     navigate(`/discover?view=${type}`);
    // };

    const syncData = useSocketSync(discoverData, user?.user_id, "discoverSync");

    // Auctions the user has won already get their own carousel above, so
    // strip them out of the main list here instead of showing them twice.
    const wonPropertyIds = useMemo(
        () =>
            new Set(
                (wonData || []).map((ele) => Number(ele?.property_id))
            ),
        [wonData]
    );
    const visibleDiscoverData = useMemo(
        () =>
            discoverData?.filter(
                (ele) => !wonPropertyIds.has(Number(ele?.property_id))
            ),
        [discoverData, wonPropertyIds]
    );

    const Wrapper = view === "map" ? "div" : React.Fragment;
    const wrapperProps =
        view === "map"
            ? {
                  className: `map-view-wrap ${isClusterPropertyListOpen ? "map-shadow" : ""}`,
              }
            : {};

    // ADREC: orphaned by the removed map view (was passed to <MapView />).
    // const setMapRef = (map) => (mapRef.current = map);
    return (
        <section className="discover-wrap">
            <div className="container py-5">
                <div className="row">
                    <div
                        className="col-lg-12 wow fadeInUp"
                        data-wow-delay="0.5s"
                    >
                        {isAuthenticated &&
                            isSeller === 0 &&
                            view !== "map" &&
                            wonData?.length > 0 && (
                                <>
                                    <div className="discover-head">
                                        <div className="heading">
                                            <h6 className="display-6">
                                                {t("Auctions You've Won")}
                                            </h6>
                                        </div>
                                    </div>
                                    {/* DiscoverCard's grid styling (image sizing, badges,
                                        price/timer overlay) all lives under the plain
                                        `.discover-list li figure ...` selector chain in
                                        _home-page.scss, so this ancestor class is required
                                        even though this list isn't a grid. */}
                                    <div className="discover-list">
                                        <WonAuctionsCarousel>
                                            {wonData.map((ele, ind) => (
                                                <DiscoverCard
                                                    ele={ele}
                                                    key={ind}
                                                    view="grid"
                                                    syncData={
                                                        wonSyncData?.filter(
                                                            ({ property_id }) =>
                                                                property_id ===
                                                                Number(
                                                                    ele?.property_id
                                                                )
                                                        )?.[0]
                                                    }
                                                />
                                            ))}
                                        </WonAuctionsCarousel>
                                    </div>
                                </>
                            )}
                        <div className="discover-head">
                            <div className="heading">
                                <h6 className="display-6">
                                    {t("Find Your Property!")}
                                </h6>
                                <p>
                                    {t(
                                        "Browse and manage all available property listings in one place"
                                    )}
                                </p>
                            </div>
                            {view !== "map" && (
                                <div className="discover-side">
                                    {/* ADREC: the Map View entry point is removed. "map" is
                                        also dropped from allowedViews above, so a stray
                                        ?view=map URL now falls back to grid. The enclosing
                                        `view !== "map"` test is left in place because the
                                        Sort control shares it. Original markup:

                                    <div
                                        className="maplink"
                                        onClick={() => handleViewChange("map")}
                                    >
                                        <button className="btn btn-white btn-sm">
                                            <img
                                                className="mr4"
                                                src="img/floor-icon.svg"
                                                alt=""
                                            />{" "}
                                            {t("Map View")}
                                        </button>
                                    </div>
                                    */}
                                    {
                                        // ADREC: the "Sort by" control is removed from the
                                        // Discover page. Line comments are used because the
                                        // block contains its own JSX comment, whose close
                                        // would terminate an enclosing block comment.
                                        // The sortBy state and sortOptions are retained --
                                        // fetchDiscover still spreads `sortBy?.api`, so the
                                        // API call is unchanged and simply sends no sort.
                                        // <div className="sort">
                                        //     {/* <select name="" id="" className="form-select">
                                        //         <option value="">Sort</option>
                                        //     </select> */}
                                        //     <ReactSelect
                                        //         name={"sort"}
                                        //         className="select select-sm"
                                        //         placeholder={t("Sort by")}
                                        //         options={sortOptions}
                                        //         classNamePrefix="custom-react-select"
                                        //         getOptionValue={(option) =>
                                        //             option.id
                                        //         }
                                        //         getOptionLabel={(option) =>
                                        //             option.title
                                        //         }
                                        //         formatOptionLabel={(option) => (
                                        //             <div
                                        //                 style={{
                                        //                     display: "flex",
                                        //                     gap: 8,
                                        //                     alignItems: "center",
                                        //                 }}
                                        //             >
                                        //                 <span>{option.icon}</span>
                                        //                 <span>
                                        //                     {t(option.title)}
                                        //                 </span>
                                        //             </div>
                                        //         )}
                                        //         onChange={(option) => {
                                        //             setSortBy(option);
                                        //             dispatch(setPage(1));
                                        //         }}
                                        //     />
                                        // </div>
                                    }
                                    {/* ADREC: the list/grid view selector is removed. The
                                        underlying `view` still resolves from the ?view=
                                        query param and defaults to grid, so existing
                                        /discover?view=grid links keep working -- only the
                                        UI toggle is gone. Original markup:

                                    <ul
                                        className="nav nav-pills"
                                        id="pills-tab1"
                                        role="tablist"
                                    >
                                        <li
                                            className="nav-item"
                                            role="presentation"
                                            onClick={() =>
                                                handleViewChange("list")
                                            }
                                        >
                                            <button
                                                className={`nav-link ${view === "list" ? "active" : ""}`}
                                                type="button"
                                            >
                                                <img
                                                    src="img/event-h-icon.svg"
                                                    alt="list-icon"
                                                />
                                            </button>
                                        </li>
                                        <li
                                            className="nav-item"
                                            role="presentation"
                                            onClick={() =>
                                                handleViewChange("grid")
                                            }
                                        >
                                            <button
                                                className={`nav-link ${view === "grid" ? "active" : ""}`}
                                                type="button"
                                            >
                                                <img
                                                    src="img/event-v-icon.svg"
                                                    alt="grid-icon"
                                                />
                                            </button>
                                        </li>
                                    </ul>
                                    */}
                                </div>
                            )}
                            {/* ADREC: the "List View" toggle only appeared while in map
                                view, which no longer exists. Original markup:

                            {view === "map" && (
                                <div
                                    className="maplink"
                                    onClick={() => handleViewChange("list")}
                                >
                                    <button className="btn btn-white">
                                        <img
                                            className="mr4"
                                            src="img/floor-icon.svg"
                                            alt=""
                                        />{" "}
                                        {t("List View")}
                                    </button>
                                </div>
                            )}
                            */}
                        </div>

                        {/* ADREC: justify-content-end right-aligns the bar now that only
                            Status and Search remain. Bootstrap's logical utility is used
                            rather than flex-end in SCSS so it mirrors correctly in RTL,
                            and so neither stylesheet needs recompiling. Original:
                            <ul className="auction-filter"> */}
                        <ul className="auction-filter justify-content-end">
                            {
                                // ADREC: the free-text "search by area or location" box is
                                // removed -- Status is the only filter on Discover now.
                                // Line comments are used because the block contains its own
                                // JSX comment, whose close would end a block comment early.
                                // <li>
                                //     <div className="filter-search">
                                //         <button className="search-btn">
                                //             <img src="img/search-icon.svg" alt="" />
                                //         </button>
                                //         {/* <input type="text" className="form-control" placeholder="Search by area or location" /> */}
                                //         <SearchInput
                                //             onSearch={(query) =>
                                //                 dispatch(
                                //                     setFilter({ search: query })
                                //                 )
                                //             }
                                //             minChars={1}
                                //             searchBoxClassName="form-control"
                                //             placeholder={t(
                                //                 "SearchByAreaOrLocation"
                                //             )}
                                //             value={search}
                                //             reset={search === "" ? true : false}
                                //         />
                                //     </div>
                                // </li>
                            }
                            {/* <li>
                                <select name="" id="" className="form-select">
                                    <option value="">Status</option>
                                </select>
                            </li>
                            <li>
                                <select name="" id="" className="form-select">
                                    <option value="">Property Type</option>
                                </select>
                            </li>
                            <li>
                                <select name="" id="" className="form-select">
                                    <option value="">Beds and Baths</option>
                                </select>
                            </li> */}

                            {/* ADREC: only the Status filter is kept on Discover. Status is
                                rendered here (rather than only in the removed All Filters
                                modal) so it stays visible on the page. The discoverFilters
                                entry for "status" already carries statusOptions inline, so
                                the generic options/onChange machinery below needs no
                                special case for it. Original allowlist:

                                .filter((f) =>
                                    [
                                        "construction_status",
                                        "property_type",
                                        "filter_beds",
                                    ].includes(f.name)
                                )
                            */}
                            {discoverFilters
                                .filter((f) => ["status"].includes(f.name))
                                .map((ele, ind) => {
                                    const options =
                                        ele.name === "district"
                                            ? districts
                                            : ele.name === "municipality"
                                              ? municipalities
                                              : ele.name === "city"
                                                ? cities
                                                : ele.name === "property_type"
                                                  ? propertyTypes
                                                  : ele.name ===
                                                      "construction_status"
                                                    ? constructionStatus
                                                    : ele.options;
                                    const selectedValue =
                                        ele.name === "filter_beds"
                                            ? filter_beds
                                            : ele.name === "filter_baths"
                                              ? filter_baths
                                              : options.find(
                                                    (opt) =>
                                                        opt.value ===
                                                        (ele.name === "district"
                                                            ? district
                                                            : ele.name ===
                                                                "municipality"
                                                              ? municipality
                                                              : ele.name ===
                                                                  "city"
                                                                ? city
                                                                : ele.name ===
                                                                    "property_type"
                                                                  ? property_type
                                                                  : // ADREC: "status" branch
                                                                    // added -- without it the
                                                                    // Status select would be
                                                                    // matched against
                                                                    // construction_status and
                                                                    // never show its selection.
                                                                    ele.name ===
                                                                      "status"
                                                                    ? status
                                                                    : construction_status)
                                                ) || "";
                                    return (
                                        <li key={ind}>
                                            <ReactSelect
                                                name={ele.name}
                                                isMulti={ele.isMulti}
                                                isClearable={false}
                                                options={options}
                                                isSearchable={true}
                                                placeholder={t(ele.placeholder)}
                                                className="select"
                                                value={selectedValue}
                                                onChange={(dropDownValues) => {
                                                    let value = ele.isMulti
                                                        ? dropDownValues
                                                        : dropDownValues.value;
                                                    dispatch(
                                                        setFilter({
                                                            [ele.name]: value,
                                                        })
                                                    );
                                                }}
                                            />
                                        </li>
                                    );
                                })}
                            {/* <li>
                                {
                                    (filter_baths.length ||
                                        filter_beds.length ||
                                        construction_status ||
                                        property_type ||
                                        city ||
                                        municipality ||
                                        district) && <button
                                            title={t("Clear all filters")}
                                            onClick={() => {
                                                dispatch(resetFilter());
                                                dispatch(
                                                    sellerAction.handleSetMunicipalities(
                                                        []
                                                    )
                                                );
                                                dispatch(
                                                    sellerAction.handleSetDistricts([])
                                                );
                                            }}
                                        >
                                        <img
                                            src="/img/close-gray.svg"
                                            alt={t("Clear all filters")}
                                        />
                                    </button>

                                }

                            </li> */}

                            {/* ADREC: the "All Filters" button is removed along with the
                                modal it opened -- every filter it held except Status is
                                gone, and Status now sits directly on the bar above.
                                Original markup:

                            <li>
                                <button
                                    className="filter-btn"
                                    onClick={() =>
                                        dispatch(
                                            toggleDiscoverModal({
                                                isFilterModalOpen: true,
                                            })
                                        )
                                    }
                                >
                                    <img src="img/filter-icon.svg" alt="" />
                                </button>
                            </li>
                            */}

                            <li style={{ width: "auto" }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={handleManualSearch}
                                >
                                    {t("Search")}
                                </button>
                            </li>
                        </ul>

                        <Wrapper {...wrapperProps}>
                            {/* ADREC: the map itself is removed. Original markup:

                            {view === "map" && (
                                <MapView
                                    view={view}
                                    data={discoverData}
                                    setMapRef={setMapRef}
                                    syncData={syncData}
                                />
                            )}
                            */}
                            <Tabs
                                view={view}
                                data={
                                    view === "map"
                                        ? clusterPropertyData
                                        : visibleDiscoverData
                                }
                                tab={discoverTabs}
                                Component={DiscoverCard}
                                className="discover-list"
                                activeTab={activeTab}
                                isLoading={discoverLoading}
                                fetchData={(key) => dispatch(setActiveTab(key))}
                                syncData={syncData}
                            />
                        </Wrapper>
                        {!discoverLoading && view !== "map" && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(page) => dispatch(setPage(page))}
                            />
                        )}
                    </div>
                    {/* ADREC: the "Unlock Your Dream Home Today!" promo banner is removed.
                        The copy lives in the image itself (public/img/ad-full-width.png),
                        not in a translation key, so removing this block is what takes it
                        off the page. The file is left on disk. Original markup:

                    {view === "grid" ? (
                        <div className="add-vertical">
                            <img src="/img/ad-full-width.png" alt="" />
                        </div>
                    ) : (
                        <></>
                    )}
                    */}
                </div>
            </div>
            {/* ADREC: the All Filters modal is removed with its button. FilterModal.jsx
                is left on disk untouched, so restoring this block plus the button above
                brings the full filter set back. Original markup:

            <FilterModal
                show={isFilterModalOpen}
                handleManualSearch={handleManualSearch}
                onClose={() =>
                    dispatch(toggleDiscoverModal({ isFilterModalOpen: false }))
                }
            />
            */}
        </section>
    );
};

export default Discover;
